import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import Quagga from "@ericblade/quagga2";
import { playBeep, unlockBeep } from "../lib/beep";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";
import { FIELD_DEFS, FORMAT_PRESETS } from "../lib/exportFormats";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function Scanner() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [user, setUser] = useState(null);
  const [counter, setCounter] = useState(0);
  const [recentCodes, setRecentCodes] = useState([]);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [alert, setAlert] = useState(null);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizeFormat, setFinalizeFormat] = useState("estandar");
  const [finalizeCustomCols, setFinalizeCustomCols] = useState([]);
  const [finalizeFieldValues, setFinalizeFieldValues] = useState({});
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [isScanning, setIsScanning] = useState(false);
  const [isScannerReady, setIsScannerReady] = useState(false);
  const [scannerPaused, setScannerPaused] = useState(false);
  const [scanMode, setScanMode] = useState("qr"); // "barcode" or "qr"
  const [manualCode, setManualCode] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const html5QrcodeRef = useRef(null);
  const quaggaActiveRef = useRef(false);
  const lastScannedCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const scanModeRef = useRef("qr");
  const scannerPausedRef = useRef(false);
  const pendingReadsRef = useRef([]); // For barcode multi-read validation

  // Keep refs in sync
  useEffect(() => { scanModeRef.current = scanMode; }, [scanMode]);
  useEffect(() => { scannerPausedRef.current = scannerPaused; }, [scannerPaused]);

  useEffect(() => {
    const token = localStorage.getItem("qre_token");
    const authHeaders = { "Authorization": `Bearer ${token}` };

    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { headers: authHeaders });
        if (response.ok) setUser(await response.json());
      } catch (error) { console.error('Error fetching user:', error); }
    };

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/session-stats`, { headers: authHeaders });
        if (response.ok) {
          const data = await response.json();
          setCounter(data.barcode_count);
        }
      } catch (error) { console.error('Error fetching stats:', error); }
    };

    fetchUser();
    fetchStats();
  }, []);

  // === BARCODE SCAN HANDLER ===
  const handleBarcodeDetected = useCallback(async (decodedText) => {
    const now = Date.now();
    if (isScanning || scannerPausedRef.current) return;
    if (lastScannedCodeRef.current === decodedText && (now - lastScanTimeRef.current) < 2000) return;

    setIsScanning(true);
    lastScannedCodeRef.current = decodedText;
    lastScanTimeRef.current = now;

    // PAUSE scanner
    setScannerPaused(true);
    if (scanModeRef.current === "barcode" && quaggaActiveRef.current) {
      Quagga.pause();
    } else if (scanModeRef.current === "qr" && html5QrcodeRef.current) {
      try { await html5QrcodeRef.current.pause(); } catch (e) {}
    }

    try {
      const token = localStorage.getItem("qre_token");
      const response = await fetch(`${API}/barcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ barcode: decodedText })
      });

      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setLastScannedCode(decodedText);
        setNextEnabled(true);
        setRecentCodes(prev => [
          { code: decodedText, time: new Date().toLocaleTimeString(), dup: data.is_duplicate },
          ...prev
        ]);

        if (navigator.vibrate) navigator.vibrate(200);
        playBeep();

        setAlert({
          type: data.is_duplicate ? 'warning' : 'success',
          message: data.is_duplicate
            ? t("duplicateCode", { code: decodedText })
            : t("scannedCode", { code: decodedText })
        });
        setTimeout(() => setAlert(null), 3000);
      } else {
        setAlert({ type: 'warning', message: t("errorSavingCode") });
      }
    } catch (error) {
      console.error('Error saving barcode:', error);
      setAlert({ type: 'warning', message: t("connErrorScanner") });
    } finally {
      setIsScanning(false);
    }
  }, [isScanning, t]);

  // === QUAGGA (BARCODE) INIT ===
  const startQuagga = useCallback(async () => {
    stopAllScanners();
    pendingReadsRef.current = [];

    return new Promise((resolve) => {
      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: document.querySelector("#reader"),
          constraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        },
        decoder: {
          readers: ["code_128_reader"],
          multiple: false
        },
        locate: true,
        locator: {
          patchSize: "large",
          halfSample: false
        },
        frequency: 10
      }, (err) => {
        if (err) {
          console.error("Quagga init error:", err);
          setAlert({ type: 'warning', message: t("cameraErrorBarcode") });
          resolve(false);
          return;
        }
        Quagga.start();
        quaggaActiveRef.current = true;
        setIsScannerReady(true);
        resolve(true);
      });

      Quagga.onDetected((result) => {
        if (!result || !result.codeResult || !result.codeResult.code) return;
        if (scannerPausedRef.current) return;

        // Calculate average error from decodedCodes
        const errors = result.codeResult.decodedCodes
          .filter(d => d.error !== undefined)
          .map(d => d.error);
        const avgError = errors.length > 0
          ? errors.reduce((a, b) => a + b, 0) / errors.length
          : 1;

        // Reject high-error reads (lower = better, threshold 0.25)
        if (avgError > 0.25) return;

        const code = result.codeResult.code;
        const reads = pendingReadsRef.current;
        reads.push(code);

        // Keep only last 5 reads
        if (reads.length > 5) reads.shift();

        // Count consecutive same reads from the end
        let consecutive = 0;
        for (let i = reads.length - 1; i >= 0; i--) {
          if (reads[i] === code) consecutive++;
          else break;
        }

        // Require 3 consecutive identical reads to accept
        if (consecutive >= 3) {
          pendingReadsRef.current = [];
          handleBarcodeDetected(code);
        }
      });
    });
  }, [handleBarcodeDetected, t]);

  // === HTML5-QRCODE (QR) INIT ===
  const startQrScanner = useCallback(async () => {
    stopAllScanners();

    try {
      const html5QrCode = new Html5Qrcode("reader");
      html5QrcodeRef.current = html5QrCode;

      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) throw new Error('No cameras found');

      let cameraId = cameras[0].id;
      const backCamera = cameras.find(camera =>
        camera.label.toLowerCase().includes('back') ||
        camera.label.toLowerCase().includes('rear') ||
        camera.label.toLowerCase().includes('environment')
      );
      if (backCamera) cameraId = backCamera.id;
      else if (cameras.length > 1) cameraId = cameras[cameras.length - 1].id;

      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            let minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            return {
              width: Math.floor(minEdge * 0.5),
              height: Math.floor(minEdge * 0.8)
            };
          },
          aspectRatio: 1.0,
          formatsToSupport: [0], // QR_CODE
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        (decodedText) => { handleBarcodeDetected(decodedText); },
        () => {}
      );
      setIsScannerReady(true);
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      setAlert({ type: 'warning', message: t("cameraErrorQr") });
    }
  }, [handleBarcodeDetected, t]);

  // === STOP ALL SCANNERS ===
  const stopAllScanners = () => {
    if (quaggaActiveRef.current) {
      try { Quagga.stop(); } catch (e) {}
      Quagga.offDetected();
      quaggaActiveRef.current = false;
    }
    if (html5QrcodeRef.current) {
      try {
        const state = html5QrcodeRef.current.getState();
        if (state === 2 || state === 3) {
          html5QrcodeRef.current.stop().then(() => {
            if (html5QrcodeRef.current) html5QrcodeRef.current.clear();
            html5QrcodeRef.current = null;
          }).catch(() => { html5QrcodeRef.current = null; });
        } else {
          html5QrcodeRef.current.clear();
          html5QrcodeRef.current = null;
        }
      } catch (e) { html5QrcodeRef.current = null; }
    }
    setIsScannerReady(false);
  };

  // === START SCANNER ON MODE CHANGE ===
  useEffect(() => {
    const timer = setTimeout(() => {
      if (scanMode === "barcode") startQuagga();
      else startQrScanner();
    }, 500);

    return () => {
      clearTimeout(timer);
      stopAllScanners();
    };
  }, [scanMode]);

  // === SWITCH MODE ===
  const toggleMode = () => {
    setScanMode(prev => prev === "barcode" ? "qr" : "barcode");
    setScannerPaused(false);
    setNextEnabled(false);
    lastScannedCodeRef.current = null;
  };

  // === SPEECH TO TEXT ===
  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setAlert({ type: 'warning', message: t("noSupportVoice") });
      setTimeout(() => setAlert(null), 3000);
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = lang === 'en' ? 'en-US' : 'es-CL';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      // Clean up: remove spaces, uppercase for codes
      transcript = transcript.replace(/\s+/g, '').toUpperCase();
      setManualCode(prev => prev + transcript);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
  };

  // === MANUAL CODE ENTRY ===
  const handleManualSubmit = async () => {
    const code = manualCode.trim();
    if (!code) return;

    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ barcode: code })
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setLastScannedCode(code);
        setNextEnabled(true);
        setRecentCodes(prev => [
          { code, time: new Date().toLocaleTimeString(), dup: data.is_duplicate },
          ...prev
        ]);
        if (navigator.vibrate) navigator.vibrate(200);
        playBeep();
        setAlert({
          type: data.is_duplicate ? 'warning' : 'success',
          message: data.is_duplicate
            ? t("duplicateCode", { code })
            : t("enteredCode", { code })
        });
        setTimeout(() => setAlert(null), 3000);
        setManualCode("");
      }
    } catch (error) {
      setAlert({ type: 'warning', message: t("connErrorScanner") });
    }
  };

  // === ACTIONS ===
  const handleDelete = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-delete-last`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setRecentCodes(prev => prev.slice(1));
        setAlert({ type: 'warning', message: t("deletedOne", { code: data.removed }) });
        setTimeout(() => setAlert(null), 2000);
        if (data.barcode_count === 0) resumeScanner();
      }
    } catch (e) { console.error(e); }
  };

  const handleRepeat = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-repeat-last`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setRecentCodes(prev => [
          { code: data.repeated, time: new Date().toLocaleTimeString(), dup: true },
          ...prev
        ]);
        if (navigator.vibrate) navigator.vibrate(100);
        playBeep();
        setAlert({ type: 'success', message: t("repeatedOne", { code: data.repeated }) });
        setTimeout(() => setAlert(null), 2000);
      }
    } catch (e) { console.error(e); }
  };

  const resumeScanner = async () => {
    setNextEnabled(false);
    setLastScannedCode(null);
    lastScannedCodeRef.current = null;
    pendingReadsRef.current = [];
    unlockBeep();
    setScannerPaused(false);

    if (scanMode === "barcode" && quaggaActiveRef.current) {
      try {
        Quagga.start();
      } catch (e) {
        // If Quagga can't resume, reinitialize
        startQuagga();
      }
    } else if (scanMode === "qr" && html5QrcodeRef.current) {
      try { await html5QrcodeRef.current.resume(); } catch (e) {}
    }
  };

  const handleOpenReview = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviewItems(data.items);
        setSelectedForDelete(new Set());
        setShowReviewModal(true);
      }
    } catch (e) { console.error(e); }
  };

  const toggleSelectItem = (index) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-delete-selected`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ indices: Array.from(selectedForDelete) })
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setSelectedForDelete(new Set());
        setAlert({ type: 'success', message: t("deletedCount", { count: data.removed_count }) });
        setTimeout(() => setAlert(null), 2500);
        const listResp = await fetch(`${API}/barcode-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (listResp.ok) {
          const listData = await listResp.json();
          setReviewItems(listData.items);
        }
        setRecentCodes(prev => {
          const remaining = data.barcodes || [];
          return remaining.slice(-5).reverse().map(code => ({ code, time: '', dup: false }));
        });
      }
    } catch (e) { console.error(e); }
  };

  const handleFinalize = () => {
    if (counter === 0) {
      setAlert({ type: 'warning', message: t("noCodesToFinalize") });
      setTimeout(() => setAlert(null), 3000);
      return;
    }
    setFinalizeFormat("estandar");
    setFinalizeCustomCols([]);
    setFinalizeFieldValues({});
    setShowFinalizeModal(true);
  };

  const toggleCustomCol = (key) => {
    setFinalizeCustomCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const confirmFinalize = async () => {
    try {
      const token = localStorage.getItem("qre_token");
      const response = await fetch(`${API}/finalize-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          format: finalizeFormat,
          extra_fields: finalizeFieldValues,
          custom_columns: finalizeCustomCols
        })
      });
      if (response.ok) {
        const data = await response.json();
        setAlert({ type: 'success', message: t("sessionFinalized", { filename: data.csv_filename }) });
        setShowFinalizeModal(false);
        setTimeout(() => {
          setCounter(0);
          setNextEnabled(false);
          setLastScannedCode(null);
          setRecentCodes([]);
          setAlert(null);
        }, 3000);
      } else {
        setAlert({ type: 'warning', message: t("errorFinalizing") });
      }
    } catch (error) {
      console.error('Error finalizing session:', error);
      setAlert({ type: 'warning', message: t("connErrorScanner") });
    }
    setShowFinalizeModal(false);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("qre_token");
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) { console.error('Error logging out:', error); }
    localStorage.removeItem("qre_token");
    localStorage.removeItem("qre_email");
    navigate('/login');
  };

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <h1>{t("headerTitle")}</h1>
        {user && (
          <div className="user-info">
            <LanguageToggle style={{ marginRight: '4px' }} />
            <span className="user-name">{user.email}</span>
            <button className="logout-button" onClick={handleLogout} data-testid="logout-button">
              {t("logout")}
            </button>
          </div>
        )}
      </div>

      <div className="counter-card">
        <div className="counter-label">{t("validReadings")}</div>
        <div className="counter-value" data-testid="barcode-counter">{counter}</div>
      </div>

      {/* MODE TOGGLE */}
      <div style={{ display: 'flex', margin: '8px 16px', borderRadius: '8px', overflow: 'hidden', border: '2px solid var(--primary-color)' }}>
        <button
          onClick={() => scanMode !== "qr" && toggleMode()}
          data-testid="mode-qr"
          style={{
            flex: 1, padding: '10px', border: 'none', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer',
            backgroundColor: scanMode === "qr" ? 'var(--primary-color)' : 'var(--white)',
            color: scanMode === "qr" ? 'var(--white)' : 'var(--primary-color)'
          }}
        >
          {t("modeQr")}
        </button>
        <button
          onClick={() => scanMode !== "barcode" && toggleMode()}
          data-testid="mode-barcode"
          style={{
            flex: 1, padding: '10px', border: 'none', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer',
            backgroundColor: scanMode === "barcode" ? 'var(--primary-color)' : 'var(--white)',
            color: scanMode === "barcode" ? 'var(--white)' : 'var(--primary-color)'
          }}
        >
          {t("modeBarcode")}
        </button>
      </div>

      {/* MANUAL CODE ENTRY */}
      <div style={{ display: 'flex', margin: '8px 16px', gap: '8px' }}>
        <input
          type="text"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
          placeholder={t("manualPlaceholder")}
          data-testid="manual-code-input"
          style={{
            flex: 1, padding: '10px 12px', borderRadius: '8px',
            border: `2px solid ${isListening ? '#e57373' : 'var(--accent-color)'}`, fontSize: '14px',
            color: 'var(--text-color)', outline: 'none'
          }}
        />
        <button
          onClick={toggleListening}
          data-testid="voice-input-button"
          style={{
            padding: '10px 12px', borderRadius: '8px', border: 'none',
            backgroundColor: isListening ? '#e57373' : '#8d6e63',
            color: 'white', fontSize: '18px', cursor: 'pointer',
            animation: isListening ? 'pulse 1s infinite' : 'none'
          }}
        >
          {isListening ? '...' : '\u{1F3A4}'}
        </button>
        <button
          onClick={handleManualSubmit}
          disabled={!manualCode.trim()}
          data-testid="manual-code-submit"
          style={{
            padding: '10px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: manualCode.trim() ? 'var(--primary-color)' : '#ccc',
            color: 'white', fontWeight: 'bold', fontSize: '14px',
            cursor: manualCode.trim() ? 'pointer' : 'not-allowed'
          }}
        >
          {t("addButton")}
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} data-testid="alert-message">
          <span>{alert.message}</span>
        </div>
      )}

      <div className="scanner-card">
        <div id="reader" style={{ width: '100%', maxWidth: '500px', margin: '0 auto', overflow: 'hidden', borderRadius: '8px' }}>
          {/* Quagga renders video here for barcode mode, html5-qrcode for QR mode */}
        </div>
        {!isScannerReady && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)', fontSize: '14px' }}>
            <p>{t("initializingScanner")}</p>
            <p>{t("allowCameraAccess")}</p>
          </div>
        )}
        {isScannerReady && (
          <div style={{
            textAlign: 'center', padding: '12px', fontSize: '13px',
            backgroundColor: scannerPaused ? '#f5e6d3' : 'var(--accent-color)',
            borderRadius: '8px', marginTop: '8px',
            color: 'var(--text-color)',
            border: scannerPaused ? '2px solid var(--error-color)' : 'none'
          }}>
            {scannerPaused ? (
              <><strong>{t("scannerPaused")}</strong><br />{t("pressNext")}</>
            ) : (
              <strong>
                {scanMode === "barcode" ? t("aimBarcode") : t("aimQr")}
              </strong>
            )}
          </div>
        )}
      </div>

      {recentCodes.length > 0 && (
        <div data-testid="recent-codes-box" style={{
          margin: '8px 16px', padding: '12px',
          backgroundColor: 'var(--white)', borderRadius: '8px',
          border: '1px solid var(--accent-color)', maxHeight: '150px', overflowY: 'auto'
        }}>
          <div style={{ fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '8px', fontSize: '14px' }}>
            {t("recentRecords")}
          </div>
          {recentCodes.slice(0, 5).map((item, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '4px 0', borderBottom: i < Math.min(recentCodes.length, 5) - 1 ? '1px solid var(--accent-color)' : 'none',
              fontSize: '13px', color: item.dup ? 'var(--error-color)' : 'var(--text-color)'
            }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                {item.code}
              </span>
              <span style={{ color: 'var(--text-light)', flexShrink: 0 }}>{item.time}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--white)', padding: '12px 16px 16px',
        boxShadow: '0 -4px 12px rgba(95, 46, 10, 0.15)'
      }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <button disabled={!nextEnabled} onClick={handleDelete} data-testid="delete-button"
            style={{ flex: 1, backgroundColor: nextEnabled ? '#e57373' : '#ccc', color: 'var(--white)', border: 'none', borderRadius: '8px', padding: '14px 0', fontSize: '14px', fontWeight: 'bold', cursor: nextEnabled ? 'pointer' : 'not-allowed' }}>
            {t("deleteButton")}
          </button>
          <button disabled={!nextEnabled} onClick={() => { setAlert(null); resumeScanner(); }} data-testid="next-button"
            style={{ flex: 2, backgroundColor: nextEnabled ? '#81c784' : '#ccc', color: 'var(--white)', border: 'none', borderRadius: '8px', padding: '14px 0', fontSize: '14px', fontWeight: 'bold', cursor: nextEnabled ? 'pointer' : 'not-allowed' }}>
            {t("nextButton")}
          </button>
          <button disabled={!nextEnabled} onClick={handleRepeat} data-testid="repeat-button"
            style={{ flex: 1, backgroundColor: nextEnabled ? '#64b5f6' : '#ccc', color: 'var(--white)', border: 'none', borderRadius: '8px', padding: '14px 0', fontSize: '14px', fontWeight: 'bold', cursor: nextEnabled ? 'pointer' : 'not-allowed' }}>
            {t("repeatButton")}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn" onClick={handleOpenReview} data-testid="review-button" disabled={counter === 0}
            style={{ flex: 1, backgroundColor: counter > 0 ? '#8d6e63' : '#ccc', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 0', fontSize: '14px', fontWeight: 'bold', cursor: counter > 0 ? 'pointer' : 'not-allowed' }}>
            {t("reviewButton")} ({counter})
          </button>
          <button className="btn btn-danger" onClick={handleFinalize} data-testid="finalize-button" style={{ flex: 1 }}>
            {t("finalizeButton")}
          </button>
          {localStorage.getItem("qre_admin") === "1" && (
            <button className="btn btn-primary" onClick={() => navigate("/downloads")} data-testid="downloads-button"
              style={{ backgroundColor: "var(--text-color)", flex: 1 }}>
              {t("downloadsButton")}
            </button>
          )}
        </div>
      </div>

      {/* FINALIZE MODAL */}
      {showFinalizeModal && (
        <div className="modal-overlay" data-testid="finalize-modal">
          <div className="modal-content" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 className="modal-title">{t("finalizeModalTitle")}</h2>
            <p className="modal-message">
              <strong>{t("totalCodes")} {counter}</strong><br /><br />
              {t("finalizeModalMessage")}
            </p>

            <p style={{ textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '8px' }}>
              {t("selectFormatTitle")}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
              {Object.keys(FORMAT_PRESETS).map((key) => (
                <button
                  key={key}
                  onClick={() => { setFinalizeFormat(key); setFinalizeFieldValues({}); }}
                  data-testid={`finalize-format-${key}`}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: '2px solid var(--primary-color)', cursor: 'pointer',
                    backgroundColor: finalizeFormat === key ? 'var(--primary-color)' : 'var(--white)',
                    color: finalizeFormat === key ? 'var(--white)' : 'var(--primary-color)',
                    fontSize: '12px', fontWeight: 'bold'
                  }}
                >
                  {lang === 'en' ? FORMAT_PRESETS[key].label_en : FORMAT_PRESETS[key].label_es}
                </button>
              ))}
            </div>

            {finalizeFormat === 'personalizado' && (
              <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>{t("customColumnsLabel")}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Object.keys(FIELD_DEFS).map((key) => (
                    <button
                      key={key}
                      onClick={() => toggleCustomCol(key)}
                      data-testid={`finalize-customcol-${key}`}
                      style={{
                        padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--accent-color)', cursor: 'pointer',
                        backgroundColor: finalizeCustomCols.includes(key) ? '#8d6e63' : 'var(--white)',
                        color: finalizeCustomCols.includes(key) ? 'white' : 'var(--text-color)',
                        fontSize: '11px'
                      }}
                    >
                      {lang === 'en' ? FIELD_DEFS[key].label_en : FIELD_DEFS[key].label_es}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(finalizeFormat === 'personalizado' ? finalizeCustomCols : FORMAT_PRESETS[finalizeFormat].fields).length > 0 && (
              <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                {(finalizeFormat === 'personalizado' ? finalizeCustomCols : FORMAT_PRESETS[finalizeFormat].fields).map((key) => (
                  <input
                    key={key}
                    type={FIELD_DEFS[key].type === 'number' ? 'number' : 'text'}
                    maxLength={FIELD_DEFS[key].maxlen || undefined}
                    placeholder={lang === 'en' ? FIELD_DEFS[key].label_en : FIELD_DEFS[key].label_es}
                    value={finalizeFieldValues[key] || ""}
                    onChange={(e) => setFinalizeFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                    data-testid={`finalize-field-${key}`}
                    className="login-input"
                    style={{ marginBottom: '8px' }}
                  />
                ))}
              </div>
            )}

            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={() => setShowFinalizeModal(false)} data-testid="cancel-finalize-button">{t("cancel")}</button>
              <button className="btn btn-danger" onClick={confirmFinalize} data-testid="confirm-finalize-button">{t("yesFinalize")}</button>
            </div>
          </div>
        </div>
      )}

      {/* REVIEW MODAL */}
      {showReviewModal && (
        <div className="modal-overlay" data-testid="review-modal" style={{ zIndex: 9999 }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '92%', maxWidth: '500px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e0d5cc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--primary-color)' }}>{t("reviewModalTitle")} ({reviewItems.length})</h2>
              <button onClick={() => setShowReviewModal(false)} data-testid="close-review-button"
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>✕</button>
            </div>
            <div style={{ padding: '12px', borderBottom: '1px solid #e0d5cc', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                {selectedForDelete.size > 0 ? `${selectedForDelete.size} ${t("selectedCount")}` : t("markToDelete")}
              </span>
              {selectedForDelete.size > 0 && (
                <button onClick={handleDeleteSelected} data-testid="delete-selected-button"
                  style={{ marginLeft: 'auto', backgroundColor: '#e57373', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                  {t("deleteButton")} ({selectedForDelete.size})
                </button>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
              {reviewItems.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>{t("noScannedCodes")}</p>
              ) : (
                reviewItems.map((item) => (
                  <div key={item.index} onClick={() => toggleSelectItem(item.index)} data-testid={`review-item-${item.index}`}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid #f0ebe6',
                      cursor: 'pointer', backgroundColor: selectedForDelete.has(item.index) ? '#ffebee' : 'transparent',
                      borderRadius: '6px', marginBottom: '2px'
                    }}>
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '4px', marginRight: '12px', flexShrink: 0,
                      border: selectedForDelete.has(item.index) ? '2px solid #e57373' : '2px solid #ccc',
                      backgroundColor: selectedForDelete.has(item.index) ? '#e57373' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {selectedForDelete.has(item.index) && <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{
                        fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: selectedForDelete.has(item.index) ? '#e57373' : 'var(--text-color)',
                        textDecoration: selectedForDelete.has(item.index) ? 'line-through' : 'none'
                      }}>{item.barcode}</div>
                      <div style={{ fontSize: '11px', color: '#999' }}>#{item.index + 1} · {item.timestamp}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scanner;
